/* OPQ Playground — pixel-art mushroom runner in the Chrome-dino visual key.
   Single-button game: spacebar / ↑ / tap-to-jump. Obstacles approach from
   the right at increasing speed. All sprites are defined as 2D string
   matrices so they're inspectable / editable inline; the renderer scales
   each "pixel" to PX (3.4× logical canvas px) so the result looks crunchy
   even on retina without external assets. */

const ROOT_SELECTOR = '.opq-playground';
const FG = '#535353';   // Chrome-dino grey on white BG

/* ============================================================
   Sprite library — each sprite is a string array. Characters:
     '.' transparent
     '#' fg (default = current fillStyle)
     'r' red accent (cigarette tip)
     'g' green tone (reserved for accent highlight)
   ============================================================ */

const SPRITES = {
  /* 14×13 Mario-style mushroom — frame A.
     Wide rounded cap with two negative-space spots, stubby stem with face
     dots (eyes) and small feet planted apart for the run cycle. */
  shroom_a: [
    '....######....',
    '..##########..',
    '.############.',
    '###..####..###',
    '###..####..###',
    '##############',
    '.############.',
    '..##########..',
    '....######....',
    '....######....',
    '...##....##...',
    '....######....',
    '...##....##...',
  ],
  /* Frame B — feet planted together (swap step). */
  shroom_b: [
    '....######....',
    '..##########..',
    '.############.',
    '###..####..###',
    '###..####..###',
    '##############',
    '.############.',
    '..##########..',
    '....######....',
    '....######....',
    '...##....##...',
    '....######....',
    '....######....',
  ],
  /* Jump pose — feet tucked under stem (airborne). */
  shroom_jump: [
    '....######....',
    '..##########..',
    '.############.',
    '###..####..###',
    '###..####..###',
    '##############',
    '.############.',
    '..##########..',
    '....######....',
    '....######....',
    '...##....##...',
    '....######....',
    '..............',
  ],

  /* 14×4 cloud — fluffy silhouette, scrolls slowly across the sky as
     decoration (does not affect gameplay). */
  cloud: [
    '...######.....',
    '.##########...',
    '##############',
    '.############.',
  ],

  /* 16×16 cannabis leaf — radial 7-spike silhouette */
  leaf: [
    '.......##.......',
    '......####......',
    '....########....',
    '...##########...',
    '..############..',
    '.##############.',
    '##############..',
    '.####.####.###..',
    '..##...##..##...',
    '...##.####.##...',
    '....######......',
    '.....####.......',
    '......##........',
    '......##........',
    '.....####.......',
    '....######......',
  ],

  /* 18×4 cigarette — paper body + red ember tip on the LEFT (so it reads
     as "moving toward you" since the obstacle slides leftward) */
  cigarette: [
    'rr################',
    'rr################',
    'rr################',
    'rr################',
  ],

  /* 12×16 letter O — chunky pixel font matching OPQ display weight */
  letterO: [
    '..########..',
    '.##########.',
    '###......###',
    '##........##',
    '##........##',
    '##........##',
    '##........##',
    '##........##',
    '##........##',
    '##........##',
    '##........##',
    '##........##',
    '##........##',
    '###......###',
    '.##########.',
    '..########..',
  ],
  /* 12×16 letter P */
  letterP: [
    '##########..',
    '###########.',
    '##........##',
    '##........##',
    '##........##',
    '##........##',
    '##........##',
    '###########.',
    '##########..',
    '##..........',
    '##..........',
    '##..........',
    '##..........',
    '##..........',
    '##..........',
    '##..........',
  ],
  /* 12×16 letter Q */
  letterQ: [
    '..########..',
    '.##########.',
    '###......###',
    '##........##',
    '##........##',
    '##........##',
    '##........##',
    '##........##',
    '##........##',
    '##........##',
    '##........##',
    '##...##...##',
    '##....##..##',
    '###....##.##',
    '.########.##',
    '..#######.##',
  ],
};

/* Map each obstacle type to a sprite + collision box. Different obstacles
   have different sizes and air-vs-ground positions. */
const OBSTACLES = [
  { type: 'leaf',    sprite: 'leaf',      airborne: false, weight: 3 },
  { type: 'cig',     sprite: 'cigarette', airborne: false, weight: 2 },
  { type: 'letterO', sprite: 'letterO',   airborne: false, weight: 1 },
  { type: 'letterP', sprite: 'letterP',   airborne: false, weight: 1 },
  { type: 'letterQ', sprite: 'letterQ',   airborne: false, weight: 1 },
];

/* ============================================================
   Renderer — each call draws one sprite at logical (x, y).
   ============================================================ */

function drawSprite(ctx, spriteName, x, y, fg) {
  const sprite = SPRITES[spriteName];
  if (!sprite) return;
  for (let row = 0; row < sprite.length; row++) {
    const line = sprite[row];
    for (let col = 0; col < line.length; col++) {
      const c = line[col];
      if (c === '.') continue;
      ctx.fillStyle = c === 'r' ? '#ff5050' : (c === 'g' ? '#3aa861' : fg);
      ctx.fillRect(x + col, y + row, 1, 1);
    }
  }
}

function spriteSize(spriteName) {
  const s = SPRITES[spriteName];
  return { w: s[0].length, h: s.length };
}

/* ============================================================
   Game
   ============================================================ */

class Game {
  constructor(root) {
    this.root = root;
    this.canvas = root.querySelector('[data-opq-pg-canvas]');
    this.scoreEl = root.querySelector('[data-opq-pg-score]');
    this.hiEl    = root.querySelector('[data-opq-pg-hi]');
    this.overlayEl = root.querySelector('[data-opq-pg-overlay]');
    this.overlayTitleEl = root.querySelector('[data-opq-pg-overlay-title]');
    this.overlaySubEl   = root.querySelector('[data-opq-pg-overlay-sub]');
    if (!this.canvas) return;

    this.ctx = this.canvas.getContext('2d');
    this.ctx.imageSmoothingEnabled = false;

    /* Logical canvas dims (set on the <canvas> width/height). The CSS
       scales the displayed size; image-rendering: pixelated keeps it
       crisp. */
    this.W = this.canvas.width;
    this.H = this.canvas.height;
    this.GROUND_Y = this.H - 10; // baseline for ground-level sprites

    /* HiScore from localStorage */
    this.hi = parseInt(localStorage.getItem('opq-playground:hi') || '0', 10);
    this.updateHi();

    /* Decorative scroll layers: dots scattered along the ground line and
       slow-moving clouds in the sky. Both wrap on the canvas width so we
       never have to spawn / cull them, just shift their X by a per-layer
       speed factor. */
    this.scrollOffset = 0;
    this.cloudOffset  = 0;
    this.groundDots = [];
    for (let i = 0; i < 36; i++) {
      this.groundDots.push({
        x: Math.random() * (this.W * 2),
        y: this.GROUND_Y + 3 + Math.floor(Math.random() * 4),
        w: 1 + Math.floor(Math.random() * 3),
      });
    }
    this.clouds = [
      { x:  40, y: 10 },
      { x: 140, y: 22 },
      { x: 240, y:  6 },
      { x: 320, y: 18 },
    ];

    this.reset();
    this.bindInput();
    this.startLoop();

    /* Initial frame so the canvas isn't blank before user starts. */
    this.draw(true);
  }

  reset() {
    this.state    = 'idle';     // 'idle' | 'running' | 'over'
    this.startedAt = 0;
    this.lastT    = 0;
    this.score    = 0;
    this.speed    = 55;         // logical px / sec (canvas is 320 wide → ~5.8s to cross)
    this.shroomY  = this.GROUND_Y - spriteSize('shroom_a').h;
    this.shroomVy = 0;
    this.jumping  = false;
    this.animT    = 0;
    this.obstacles = [];
    this.spawnTimer = 0;
    this.spawnInterval = 1.4;   // seconds between spawns (decreases)
    this.scoreTickT = 0;

    this.updateScore(0);
    this.showOverlay(this.overlayTitleEl?.dataset?.startTitle || 'PRESS SPACE / TAP TO START',
                     this.overlaySubEl?.dataset?.startSub || 'jump the leaves, cigarettes & O · P · Q');
  }

  /* === Loop === */
  startLoop() {
    const tick = (now) => {
      if (!this.lastT) this.lastT = now;
      const dt = Math.min((now - this.lastT) / 1000, 0.05);
      this.lastT = now;
      if (this.state === 'running') this.update(dt);
      this.draw();
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  /* === Input === */
  bindInput() {
    const handleAction = (e) => {
      e.preventDefault();
      if (this.state === 'idle' || this.state === 'over') {
        this.start();
      } else if (this.state === 'running') {
        this.jump();
      }
    };

    window.addEventListener('keydown', (e) => {
      if (e.code === 'Space' || e.code === 'ArrowUp' || e.code === 'KeyW') {
        if (e.repeat) return;
        handleAction(e);
      }
    });

    /* Pointer (mouse + touch) — listen on the section so taps near the
       canvas all count, but skip if the tap is on a header link. */
    this.root.addEventListener('pointerdown', (e) => {
      if (e.target.closest('a, button, input')) return;
      handleAction(e);
    });
  }

  start() {
    if (this.state === 'over') this.reset();
    this.state = 'running';
    this.startedAt = performance.now();
    this.hideOverlay();
    /* Start with an immediate jump for tactile feedback */
    this.jump();
  }

  jump() {
    /* Only allow jump if grounded */
    const groundedY = this.GROUND_Y - spriteSize('shroom_a').h;
    if (this.shroomY >= groundedY - 1) {
      this.shroomVy = -210; // px/s upward (peak ~46px ≈ 3× obstacle height)
      this.jumping = true;
    }
  }

  /* === Update === */
  update(dt) {
    /* Snail physics */
    const groundedY = this.GROUND_Y - spriteSize('shroom_a').h;
    this.shroomY += this.shroomVy * dt;
    this.shroomVy += 480 * dt; // gravity (jump duration ≈ 0.87s @ -210 v0)
    if (this.shroomY >= groundedY) {
      this.shroomY = groundedY;
      this.shroomVy = 0;
      this.jumping = false;
    }

    /* Gradually accelerate game speed and spawn rate */
    this.speed += 2.5 * dt;
    this.spawnInterval = Math.max(0.6, 1.4 - this.score / 800);

    /* Parallax scrolling for ground dots (full speed) and clouds (1/4
       speed so the sky drifts slowly behind the action). */
    this.scrollOffset += this.speed * dt;
    this.cloudOffset  += this.speed * 0.25 * dt;

    /* Spawn obstacles */
    this.spawnTimer += dt;
    if (this.spawnTimer >= this.spawnInterval) {
      this.spawnTimer = 0;
      this.spawnObstacle();
    }

    /* Move and cull obstacles */
    for (let i = this.obstacles.length - 1; i >= 0; i--) {
      const ob = this.obstacles[i];
      ob.x -= this.speed * dt;
      if (ob.x + ob.w < 0) this.obstacles.splice(i, 1);
    }

    /* Animate shroom frame */
    this.animT += dt;

    /* Score: 10 pts/sec */
    this.scoreTickT += dt;
    if (this.scoreTickT >= 0.1) {
      this.scoreTickT = 0;
      this.updateScore(this.score + 1);
    }

    /* Collision */
    this.checkCollision();
  }

  spawnObstacle() {
    /* Weighted random pick */
    const total = OBSTACLES.reduce((s, o) => s + o.weight, 0);
    let r = Math.random() * total;
    let pick = OBSTACLES[0];
    for (const o of OBSTACLES) {
      r -= o.weight;
      if (r <= 0) { pick = o; break; }
    }
    const sz = spriteSize(pick.sprite);
    const y = this.GROUND_Y - sz.h;
    this.obstacles.push({
      ...pick,
      x: this.W + 4,
      y,
      w: sz.w,
      h: sz.h,
    });
  }

  checkCollision() {
    const lz = spriteSize(this.jumping ? 'shroom_jump' : 'shroom_a');
    const lx = 16;
    const ly = this.shroomY;
    /* Shrink shroom hitbox 1.5px each side for fairness */
    const lzBox = { x: lx + 2, y: ly + 1, w: lz.w - 4, h: lz.h - 2 };

    for (const ob of this.obstacles) {
      const obBox = { x: ob.x + 1, y: ob.y + 1, w: ob.w - 2, h: ob.h - 2 };
      if (lzBox.x < obBox.x + obBox.w && lzBox.x + lzBox.w > obBox.x &&
          lzBox.y < obBox.y + obBox.h && lzBox.y + lzBox.h > obBox.y) {
        this.gameOver();
        return;
      }
    }
  }

  gameOver() {
    this.state = 'over';
    if (this.score > this.hi) {
      this.hi = this.score;
      try { localStorage.setItem('opq-playground:hi', String(this.hi)); } catch {}
      this.updateHi();
    }
    this.showOverlay('GAME OVER', `score ${this.score} · tap to restart`);
  }

  /* === Draw === */
  draw(initial = false) {
    const ctx = this.ctx;
    ctx.clearRect(0, 0, this.W, this.H);

    /* Sky: parallax-scrolled clouds (slow). */
    const cloudW = spriteSize('cloud').w;
    for (const c of this.clouds) {
      const wrap = this.W + cloudW;
      const x = ((c.x - this.cloudOffset) % wrap + wrap) % wrap - cloudW;
      drawSprite(ctx, 'cloud', Math.round(x), c.y, FG);
    }

    /* Ground baseline + scattered dots (Chrome-dino texture). */
    ctx.fillStyle = FG;
    ctx.fillRect(0, this.GROUND_Y + 1, this.W, 1);
    for (const d of this.groundDots) {
      const wrap = this.W * 2;
      const x = ((d.x - this.scrollOffset) % wrap + wrap) % wrap;
      if (x < this.W) ctx.fillRect(Math.floor(x), d.y, d.w, 1);
    }

    /* Snail */
    const lzSprite = this.jumping ? 'shroom_jump'
      : (Math.floor(this.animT * 10) % 2 === 0 ? 'shroom_a' : 'shroom_b');
    drawSprite(ctx, lzSprite, 16, this.shroomY, FG);

    /* Obstacles */
    for (const ob of this.obstacles) {
      drawSprite(ctx, ob.sprite, Math.round(ob.x), Math.round(ob.y), FG);
    }

    if (initial) return;
  }

  /* === HUD helpers === */
  updateScore(n) {
    this.score = n;
    if (this.scoreEl) this.scoreEl.textContent = String(n).padStart(4, '0');
  }
  updateHi() {
    if (this.hiEl) this.hiEl.textContent = `HI ${String(this.hi).padStart(4, '0')}`;
  }
  showOverlay(title, sub) {
    if (this.overlayTitleEl) this.overlayTitleEl.textContent = title;
    if (this.overlaySubEl)   this.overlaySubEl.textContent = sub;
    if (this.overlayEl)      this.overlayEl.classList.remove('is-hidden');
  }
  hideOverlay() {
    if (this.overlayEl) this.overlayEl.classList.add('is-hidden');
  }
}

document.querySelectorAll(ROOT_SELECTOR).forEach((root) => new Game(root));
